import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import clsx from 'clsx'
import Dialog from '@material-ui/core/Dialog'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import Input from '@material-ui/core/Input'
import InputLabel from '@material-ui/core/InputLabel'
import Alert from 'ui/components/alert'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import Typography from '@material-ui/core/Typography'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormHelperText from '@material-ui/core/FormHelperText'
import IconButton from '@material-ui/core/IconButton'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as IconClose } from 'assets/icons/close.svg'
import { ReactComponent as IconEmail } from 'assets/icons/email.svg'
import { ReactComponent as IconArrowDown } from 'assets/icons/arrow-down.svg'
import Grid from '@material-ui/core/Grid'
import useTranslation from 'hooks/useTranslations'
import useStyles from './styles'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import AutocompleteChips from 'ui/components/autocomplete/autocompleteChips'

const AddVendor = props => {
  const {
    isOpen,
    genericError,
    isConfirmed,
    cifError,
    emailError,
    phoneError,
    onClose,
    onSubmit,
    showNewVendor,
    countries,
    vendorTypesOptions,
  } = props

  const t = useTranslation()
  const classes = useStyles()
  const [cif, setCif] = useState('')
  const [showCifError, setShowCifError] = useState(false)
  const [showEmailError, setShowEmailError] = useState(false)
  const [showPhoneError, setShowPhoneError] = useState(false)
  const [vendorTypes, setVendorTypes] = useState([])
  const [country, setCountry] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [phone, setPhone] = useState('')
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'))
  const dialogRef = React.useRef(null)

  const handleCifChange = event => {
    setCif(event.target.value)
  }

  const handleCifFocus = () => {
    setShowCifError(false)
  }

  const handleEmailFocus = () => {
    setShowEmailError(false)
  }

  const handlePhoneFocus = () => {
    setShowPhoneError(false)
  }

  const handleSubmit = event => {
    event.preventDefault()
    setShowCifError(true)
    setShowEmailError(true)
    setShowPhoneError(true)

    onSubmit({
      cif,
      vendorTypes,
      country,
      email,
      companyName,
      phone,
    })
  }

  const handleCountryChange = (_, value) => {
    setCountry(value ? value.code : '')
    setShowCifError(false)
  }

  const handleEmailChange = event => {
    setEmail(event.target.value)
  }

  const handleCompanyNameChange = event => {
    setCompanyName(event.target.value)
  }

  const handlePhoneChange = event => {
    setPhone(event.target.value)
  }

  const isFormDisabled = useMemo(() => {
    return showNewVendor
      ? !cif ||
          !country ||
          !companyName ||
          !email ||
          (cifError && showCifError) ||
          (emailError && showEmailError) ||
          (phone && phoneError && showPhoneError) ||
          false
      : !cif
  }, [
    cif,
    cifError,
    companyName,
    country,
    email,
    emailError,
    phone,
    phoneError,
    showCifError,
    showEmailError,
    showPhoneError,
    showNewVendor,
  ])

  useEffect(() => {
    if (cifError && showCifError) {
      dialogRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [cifError, showCifError])

  return (
    <Dialog
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      className={clsx(isSmall && classes.dialogXS)}
      fullScreen={isSmall && showNewVendor}
    >
      <DialogTitle
        disableTypography
        id="customized-dialog-title"
        className={classes.dialogTitle}
        onClose={onClose}
      >
        {showNewVendor ? (
          <Typography variant="h2">{t('vendors:add-new')}</Typography>
        ) : (
          <Typography variant="h2">{t('vendor:add')}</Typography>
        )}

        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.dialogClose}
            onClick={onClose}
          >
            <SvgIcon component={IconClose} />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent} ref={dialogRef}>
        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          {genericError ? (
            <Alert extraClass={classes.alert} severity="error">
              {genericError}
            </Alert>
          ) : null}
          <FormControl fullWidth className={classes.formControl}>
            <InputLabel htmlFor="cif" shrink>
              {t('vendors:cif')}
            </InputLabel>
            <Input
              id="cif"
              required
              autoFocus
              error={!!(showCifError && cifError)}
              className={clsx(
                isConfirmed && classes.textConfirmed,
                cif && 'hasValue',
              )}
              value={cif}
              onChange={handleCifChange}
              onFocus={handleCifFocus}
              disableUnderline
            />
            {showCifError && cifError ? (
              <FormHelperText error={true}>
                {t('error:invalid-cif')}
              </FormHelperText>
            ) : null}
          </FormControl>
          {showNewVendor ? (
            <Alert extraClass={classes.alert} severity="info">
              {t('vendors:complete-info')}
            </Alert>
          ) : null}
          {vendorTypesOptions && vendorTypesOptions.length ? (
            <Box mb={3}>
              <InputLabel htmlFor="vendor-types" shrink>
                {t('search:vendor-types')}
              </InputLabel>
              <AutocompleteChips
                id="vendor-types"
                className={`${vendorTypes ? classes.hasValue : ''}`}
                options={vendorTypesOptions}
                placeholder={t('search:select-vendor-types')}
                value={vendorTypes}
                setValue={setVendorTypes}
                noOptionsText={t('search:no-options-vendor-types')}
                applyWrap={true}
              />
            </Box>
          ) : null}
          {showNewVendor ? (
            <>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel htmlFor="company" shrink>
                  {t('vendors:company')}
                </InputLabel>
                <Input
                  id="company"
                  required
                  className={clsx(
                    isConfirmed && classes.textConfirmed,
                    companyName && 'hasValue',
                  )}
                  value={companyName}
                  onChange={handleCompanyNameChange}
                  disableUnderline
                />
              </FormControl>
              <Box mb={3}>
                <InputLabel htmlFor="country" shrink>
                  {t('vendors:country')}
                </InputLabel>
                <Autocomplete
                  id="country"
                  fullWidth
                  className={`${country ? classes.hasValue : ''}`}
                  popupIcon={<SvgIcon component={IconArrowDown} />}
                  options={countries}
                  onChange={handleCountryChange}
                  getOptionLabel={option => option.name || ''}
                  renderInput={params => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                      placeholder={t('vendors:country-placeholder')}
                      variant="outlined"
                    />
                  )}
                />
              </Box>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel htmlFor="email" shrink>
                  {t('vendors:email')}
                </InputLabel>
                <Input
                  required
                  id="email"
                  className={clsx(email && 'hasValue')}
                  value={email}
                  error={!!(showEmailError && emailError)}
                  onFocus={handleEmailFocus}
                  onChange={handleEmailChange}
                  disableUnderline
                  endAdornment={
                    <InputAdornment position="end">
                      <SvgIcon component={IconEmail} />
                    </InputAdornment>
                  }
                />
                {showEmailError && emailError ? (
                  <FormHelperText error={true}>
                    {t('error:invalid-email')}
                  </FormHelperText>
                ) : null}
              </FormControl>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel htmlFor="phone" shrink>
                  {t('vendors:phone')}
                </InputLabel>
                <Input
                  required
                  id="phone"
                  className={clsx(phone && 'hasValue')}
                  value={phone}
                  error={!!(showPhoneError && phoneError)}
                  onFocus={handlePhoneFocus}
                  onChange={handlePhoneChange}
                  disableUnderline
                />
                <FormHelperText>{t('vendors:phone-helper')}</FormHelperText>
                {showPhoneError && phoneError ? (
                  <FormHelperText error={true}>
                    {t('error:invalid-phone')}
                  </FormHelperText>
                ) : null}
              </FormControl>
            </>
          ) : null}
          <Grid container justify="flex-end">
            <Grid item>
              <Button
                type="submit"
                disabled={isFormDisabled}
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {t('vendor:add')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  )
}

AddVendor.propTypes = {
  isOpen: PropTypes.bool,
  genericError: PropTypes.bool,
  isConfirmed: PropTypes.bool,
  cifError: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onResetClick: PropTypes.func,
  showNewVendor: PropTypes.bool,
  countries: PropTypes.array,
}

export default AddVendor
